import { useTranslation } from 'react-i18next'
import {
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Button,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import DamageDuringRouteFooter from './Footer'
import PhotoPreviewRow from './PhotoPreviewRow'
import { useState, useRef, useEffect } from 'react'
import useFormErrorScroller from '../../hooks/useFormErrorScroller'
import { DAMAGE_DURING_ROUTE_STEPS } from './constants'
import { useSendDamageDuringRoute } from './useSendDamageDuringRoute'
import useDamageTypes from '../../hooks/damageRegistration/useDamageTypes'
import { useAppStore } from '../../store/useAppStore'
import { DamageDuringRouteProps } from './types'
import { useParamsFromURL } from '../../hooks/useParamsFromURL'
import { getStepOneSchema, stepZeroSchema } from './schema'
import { useVehicleDataQuery } from '../../hooks/queries/useVehicleDataQuery'
import { fileListToBase64Array } from '../../lib/helpers'
import { DamageDuringRouteRequest } from './types'
import { VEHICLE_SIDES } from '../../constants'
import StepZero from './StepZero'
import StepOne from './StepOne'
import FieldNames from './fieldNames'
import { SectionSubtitle1 } from './SectionTypography'

const navHeaderHeight = 57
const footerHeight = 70

function mergeFiles(
  existingFiles: FileList | null,
  newFiles: FileList
): FileList {
  const dataTransfer = new DataTransfer()
  if (existingFiles) {
    Array.from(existingFiles).forEach((file) => dataTransfer.items.add(file))
  }
  Array.from(newFiles).forEach((file) => dataTransfer.items.add(file))
  return dataTransfer.files
}

function removeFileAtIndex(
  files: FileList | null,
  indexToRemove: number
): FileList {
  if (!files) return new DataTransfer().files
  const dataTransfer = new DataTransfer()
  Array.from(files).forEach((file, index) => {
    if (index !== indexToRemove) {
      dataTransfer.items.add(file)
    }
  })
  return dataTransfer.files
}

const formatDateTimeDutch = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const datePart = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d)
  const timePart = new Intl.DateTimeFormat('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d)
  return `${datePart} om ${timePart}`
}

export type DamageDuringRouteFormState = {
  injured: boolean | null
  damageLocation: { own: boolean; other: boolean; object: boolean }
  involved: boolean | null
  counterpartyContact: string
  whatHappened: string
  licensePlate: string
  vehicleOperational: boolean
  damageClassification: string
  damageSide: string
  vehicleCoordinates: [number, number] | null
  photosOwn: FileList
  photosOther: FileList
  photosObject: FileList
  photosEnvironment: FileList
  formPhoto: FileList
  kibNumber: string
  damageTargets: string[]
  damageReport: string
}

function DamageDuringRoute({ onStepValidChange }: DamageDuringRouteProps) {
  const { t } = useTranslation()
  const tabValue = useAppStore((state) => state.tabValue)
  const setSnackbar = useAppStore((state) => state.setSnackbar)
  const { damageTypeOptions } = useDamageTypes(tabValue)
  useVehicleDataQuery()

  const {
    lat,
    lng,
    pNumber: urlPNumber,
    vehicleId,
    depotCode,
  } = useParamsFromURL(['lat', 'lng', 'pNumber', 'vehicleId', 'depotCode'])

  const [formData, setFormData] = useState<DamageDuringRouteFormState>({
    injured: null as boolean | null,
    damageLocation: {
      own: true,
      other: false,
      object: false,
    },
    involved: null as boolean | null,
    counterpartyContact: '',
    whatHappened: '',
    licensePlate: '',
    vehicleOperational: false,
    damageClassification: '',
    damageSide: VEHICLE_SIDES[0],
    vehicleCoordinates: null,
    photosOwn: new DataTransfer().files,
    photosOther: new DataTransfer().files,
    photosObject: new DataTransfer().files,
    photosEnvironment: new DataTransfer().files,
    formPhoto: new DataTransfer().files,
    kibNumber: '',
    damageTargets: [] as string[],
    damageReport: '',
  })

  const PHOTO_MIN = 1
  const PHOTO_MAX = 3

  const stepZeroForm = useForm({
    resolver: zodResolver(stepZeroSchema),
    defaultValues: {
      injured: formData.injured,
    },
    mode: 'onSubmit',
  })

  const stepOneForm = useForm({
    resolver: zodResolver(getStepOneSchema()),
    defaultValues: {
      damageLocation: {
        own: true,
        other: false,
        object: false,
      },
      involved: formData.involved,
      counterpartyContact: formData.counterpartyContact,
      whatHappened: formData.whatHappened,
      licensePlate: formData.licensePlate,
      vehicleOperational: formData.vehicleOperational,
      damageClassification: formData.damageClassification,
      damageSide: formData.damageSide,
      vehicleCoordinates: formData.vehicleCoordinates,
      [FieldNames.PhotosOwn]: formData.photosOwn,
      [FieldNames.PhotosOther]: formData.photosOther,
      [FieldNames.PhotosObject]: formData.photosObject,
      [FieldNames.PhotosEnvironment]: formData.photosEnvironment,
      [FieldNames.FormPhoto]: formData.formPhoto,
    },
    mode: 'onSubmit',
  })

  const headerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const nestedContentRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  const { registerField, scrollToField, getFirstErrorPath, resetFields } =
    useFormErrorScroller(contentRef)

  const [activeStep, setActiveStep] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const timestampRef = useRef<string>(new Date().toISOString())
  const steps = DAMAGE_DURING_ROUTE_STEPS
  const { mutate: sendDamage, isLoading } = useSendDamageDuringRoute()

  const stepZeroData = stepZeroForm.watch()
  const stepOneData = stepOneForm.watch()

  useEffect(() => {
    const onScroll = (event: any) => {
      const contentChildStyles = window.getComputedStyle(
        nestedContentRef.current!
      )
      const contentChildPaddingBottom =
        contentChildStyles.getPropertyValue('padding-bottom')

      /* Check if the content has enough height to be able to scroll to the end of the window.
       If no: add necessary bottom padding, else: keep bottom padding equal to the footer height */
      if (window.innerHeight - contentRef.current!.scrollHeight > 0) {
        const necessaryHeight =
          window.innerHeight - contentRef.current!.scrollHeight
        nestedContentRef.current!.style.paddingBottom = `calc(${contentChildPaddingBottom} + ${necessaryHeight}px)`
      } else if (window.innerHeight - contentRef.current!.scrollHeight < 0) {
        nestedContentRef.current!.style.paddingBottom = `${footerHeight}px`
      }

      // handle header visibility
      if (event.currentTarget.scrollTop) {
        headerRef.current!.style.display = 'none'
      } else {
        headerRef.current!.style.display = 'block'
      }

      // handle footer visibility and content scroll position
      if (
        event.currentTarget.scrollTop <
        event.currentTarget.scrollHeight -
        event.currentTarget.clientHeight -
        footerHeight
      ) {
        if (footerRef.current?.style.display !== 'none') {
          event.currentTarget.scrollTop -= footerHeight
        }
        footerRef.current!.style.display = 'none'
      } else {
        if (footerRef.current?.style.display !== 'block') {
          event.currentTarget.scrollTop += footerHeight
        }
        footerRef.current!.style.display = 'block'
      }
    }

    contentRef.current?.addEventListener('scroll', onScroll)

    return () => {
      contentRef.current?.removeEventListener('scroll', onScroll)
    }
  }, [])

  useEffect(() => {
    if (contentRef.current) {
      try {
        contentRef.current.scrollTo({ top: 0, behavior: 'auto' })
      } catch (e) {
        contentRef.current.scrollTop = 0
      }
    }
    resetFields()
  }, [activeStep, resetFields])

  const handleDeletePhoto = (
    watchedForm: DamageDuringRouteFormState,
    photoType: keyof typeof formData,
    index: number
  ) => {
    const updatedFiles = removeFileAtIndex(
      watchedForm[photoType] as FileList,
      index
    )

    if (
      photoType === FieldNames.PhotosOwn ||
      photoType === FieldNames.PhotosOther ||
      photoType === FieldNames.PhotosObject ||
      photoType === FieldNames.PhotosEnvironment ||
      photoType === FieldNames.FormPhoto
    ) {
      stepOneForm.setValue(photoType as any, updatedFiles)
    }

    setFormData((prev) => ({
      ...prev,
      [photoType]: updatedFiles,
    }))
  }

  const handleStepZeroSubmit = stepZeroForm.handleSubmit(async (data) => {
    const updatedFormData = {
      ...formData,
      injured: data.injured,
    }
    setFormData(updatedFormData)
    setActiveStep(1)
    if (onStepValidChange) onStepValidChange(true)
  })

  const handleStepOneSubmit = stepOneForm.handleSubmit(
    async (data) => {
      const updatedFormData = {
        ...formData,
        ...data,
      }
      setFormData(updatedFormData)
      setActiveStep(2)
      if (onStepValidChange) onStepValidChange(true)
    },
    (errors) => {
      const first = getFirstErrorPath(errors)
      scrollToField(first)
    }
  )

  const handleFinalSubmit = async () => {
    const finalPNumber = urlPNumber
    const picturesBase64 = await fileListToBase64Array(formData.photosOwn)
    const picturesEnvironmentBase64 = await fileListToBase64Array(
      formData.photosEnvironment
    )
    const picturesObjectBase64 = await fileListToBase64Array(
      formData.photosObject
    )
    const picturesCounterPartyBase64 = await fileListToBase64Array(
      formData.photosOther
    )
    const formPhotoBase64 = await fileListToBase64Array(formData.formPhoto)

    const payload: DamageDuringRouteRequest = {
      depotCode: depotCode as string,
      // Send vehicleId as licensePlate
      licensePlate: vehicleId || '',
      // Send licensePlate as counterPartyLicensePlate
      // Omit it entirely when empty — the backend validates the format and rejects ''
      counterPartyLicensePlate: formData.licensePlate || undefined,
      timestamp: timestampRef.current,
      pNumber: finalPNumber as string,
      xCoordinate: parseInt(String(formData.vehicleCoordinates?.[0] || ''), 10),
      yCoordinate: parseInt(String(formData.vehicleCoordinates?.[1] || ''), 10),
      description: formData.whatHappened || undefined,
      classifications: formData.damageClassification
        ? [formData.damageClassification]
        : undefined,
      side: formData.damageSide,
      status: 'nieuw',
      voortgang: undefined,
      contactPerson: formData.counterpartyContact || undefined,
      picturesBase64,
      scenePhotosBase64: picturesEnvironmentBase64 || [],
      claimUploaded: true,
      kibNumber: formData.kibNumber || undefined,
      damageTargets: [
        'ownvehicle',
        ...(formData.damageLocation.other ? ['thirdparty'] : []),
        ...(formData.damageLocation.object ? ['externalobject'] : []),
      ],
      counterPartyPresent: formData.involved === true,
      // prefer damageReport, otherwise use first form photo (base64) if available
      damageReport:
        formData.damageReport ||
        (formPhotoBase64 && formPhotoBase64.length
          ? formPhotoBase64[0]
          : undefined),
      vehicleOperational: formData.vehicleOperational || false,
      picturesObjectBase64,
      picturesCounterPartyBase64: picturesCounterPartyBase64,
      locationGeo: {
        latitude: parseFloat(lat as string),
        longitude: parseFloat(lng as string),
      },
      ...(formData.involved === true && {
        // Use the courier's current location if there is a counterparty involved
        locationByCourierGeo: {
          latitude: parseFloat(lat as string),
          longitude: parseFloat(lng as string),
        },
      }),
    }

    sendDamage(payload, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: t('damage_during_route.submit_success'),
        })
        setSubmitted(true)
      },
      onError: (error: any) => {
        setSnackbar({
          open: true,
          message: t('damage_during_route.submit_error'),
        })
        console.error('Failed to submit damage report:', error)
      },
    })
  }

  const handleBackStep = () => {
    if (activeStep === 1) {
      stepOneForm.reset({
        damageLocation: { own: true, other: false, object: false },
        involved: null,
        counterpartyContact: '',
        whatHappened: '',
        licensePlate: '',
        vehicleOperational: false,
        damageClassification: '',
        vehicleCoordinates: null,
        photosOther: new DataTransfer().files,
        photosOwn: new DataTransfer().files,
        photosObject: new DataTransfer().files,
        photosEnvironment: new DataTransfer().files,
        formPhoto: new DataTransfer().files,
        damageSide: '',
        kibNumber: '',
      })
    }

    setActiveStep((prev) => prev - 1)
  }

  if (submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('damage_during_route.submit_success')}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          {t('common.back_to_start')}
        </Button>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        overflow: 'hidden',
        height: `calc(100vh - ${navHeaderHeight}px)`,
        position: 'relative',
      }}
    >
      <Box
        ref={headerRef}
        sx={{
          flex: '0',
          width: '100%',
          mx: 'auto',
          pt: 2,
          px: '24px',
          boxSizing: 'border-box',
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{ mb: 2, bgcolor: 'transparent' }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box
        ref={contentRef}
        sx={{
          flex: '1',
          width: '100%',
          maxWidth: '768px',
          mx: 'auto',
          p: 3,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 2,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Box
          ref={nestedContentRef}
          sx={{
            width: '100%',
            maxWidth: '100%',
            paddingBottom: `${footerHeight}px`,
          }}
        >
          {activeStep === 0 && (
            <StepZero
              control={stepZeroForm.control}
              watchedForm={stepZeroData}
            />
          )}
          {activeStep === 1 && (
            <>
              <StepOne
                control={stepOneForm.control}
                watchedForm={stepOneData}
                setValue={stepOneForm.setValue}
                damageTypeOptions={
                  (damageTypeOptions || []) as [string, string][]
                }
                mergeFiles={mergeFiles}
                PHOTO_MIN={PHOTO_MIN}
                PHOTO_MAX={PHOTO_MAX}
                onDeletePhoto={handleDeletePhoto}
                registerFieldRef={registerField}
              />
            </>
          )}
          {activeStep === 2 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('damage_during_route.summary_title')}
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                <li>
                  {t('damage_during_route.summary_date', {
                    date: formatDateTimeDutch(timestampRef.current),
                  })}
                </li>
                <li>
                  {t('damage_during_route.summary_injured')}:{' '}
                  {formData.injured === true ? t('common.yes') : t('common.no')}
                </li>
                <li>
                  {formData.involved === true
                    ? t('damage_during_route.summary_involved')
                    : t('damage_during_route.summary_not_involved')}
                </li>
                {formData.damageLocation.own && (
                  <li>{t('damage_during_route.summary_own_vehicle')}</li>
                )}
                {formData.damageLocation.other && (
                  <li>
                    {t('damage_during_route.summary_other_vehicle', {
                      license_plate: formData.licensePlate,
                    })}
                  </li>
                )}
                {formData.damageLocation.object && (
                  <li>{t('damage_during_route.summary_object')}</li>
                )}
                {formData.damageLocation.own &&
                  formData.damageClassification && (
                    <li>
                      {t('damage_during_route.summary_damage_classification', {
                        classification:
                          damageTypeOptions.find(
                            ([, value]) =>
                              value === formData.damageClassification
                          )?.[0] || formData.damageClassification,
                      })}
                    </li>
                  )}
                {formData.damageLocation.own && formData.damageSide && (
                  <li>
                    {t('damage_during_route.summary_damage_side', {
                      side:
                        t(`vehicle_side.${formData.damageSide}`) ||
                        formData.damageSide,
                    })}
                  </li>
                )}
              </Box>

              {formData.involved === true
                ? // Show photos if provided; else show counterparty text if provided; else render nothing
                formData[FieldNames.FormPhoto] &&
                formData[FieldNames.FormPhoto].length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                    >
                      {t('damage_during_route.damage_form_fill')}
                    </Typography>
                    <Box sx={{ bgcolor: '#F7F7F7', p: 2, borderRadius: 2 }}>
                      <PhotoPreviewRow
                        files={formData[FieldNames.FormPhoto] as FileList}
                      />
                    </Box>
                  </Box>
                )
                : Boolean(formData.whatHappened) && (
                  <Box sx={{ mb: 3 }}>
                    <SectionSubtitle1>
                      {t('damage_during_route.what_happened')}
                    </SectionSubtitle1>
                    <Box
                      sx={{
                        p: 2,
                        px: 0,
                      }}
                    >
                      {formData.whatHappened}
                    </Box>
                  </Box>
                )}
              {((formData[FieldNames.PhotosOwn] &&
                formData[FieldNames.PhotosOwn].length > 0) ||
                (formData[FieldNames.PhotosOther] &&
                  formData[FieldNames.PhotosOther].length > 0) ||
                (formData[FieldNames.PhotosObject] &&
                  formData[FieldNames.PhotosObject].length > 0) ||
                (formData[FieldNames.PhotosEnvironment] &&
                  formData[FieldNames.PhotosEnvironment].length > 0)) && (
                  <Box sx={{ mb: 3, pointerEvents: 'none' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                    >
                      {t('damage_registration_page.photos')}
                    </Typography>

                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      {formData[FieldNames.PhotosOwn] &&
                        formData[FieldNames.PhotosOwn].length > 0 && (
                          <Box sx={{ bgcolor: '#F7F7F7', p: 2, borderRadius: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              {t('damage_during_route.photo_situation_own')}
                            </Typography>
                            <PhotoPreviewRow
                              files={formData[FieldNames.PhotosOwn] as FileList}
                            />
                          </Box>
                        )}

                      {formData[FieldNames.PhotosOther] &&
                        formData[FieldNames.PhotosOther].length > 0 && (
                          <Box sx={{ bgcolor: '#F7F7F7', p: 2, borderRadius: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              {t('damage_during_route.photo_situation_other')}
                            </Typography>
                            <PhotoPreviewRow
                              files={formData[FieldNames.PhotosOther] as FileList}
                            />
                          </Box>
                        )}

                      {formData[FieldNames.PhotosObject] &&
                        formData[FieldNames.PhotosObject].length > 0 && (
                          <Box sx={{ bgcolor: '#F7F7F7', p: 2, borderRadius: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              {t('damage_during_route.object_photo')}
                            </Typography>
                            <PhotoPreviewRow
                              files={
                                formData[FieldNames.PhotosObject] as FileList
                              }
                            />
                          </Box>
                        )}

                      {formData[FieldNames.PhotosEnvironment] &&
                        formData[FieldNames.PhotosEnvironment].length > 0 && (
                          <Box sx={{ bgcolor: '#F7F7F7', p: 2, borderRadius: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              {t(
                                'damage_during_route.photo_situation_environment'
                              )}
                            </Typography>
                            <PhotoPreviewRow
                              files={
                                formData[FieldNames.PhotosEnvironment] as FileList
                              }
                            />
                          </Box>
                        )}
                    </Box>
                  </Box>
                )}
            </>
          )}
        </Box>
      </Box>
      <Box
        ref={footerRef}
        sx={{
          flex: '0',
          py: 2,
          px: 1,
          borderTop: '1px solid #e0e0e0',
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 768,
          backgroundColor: '#ffffff',
        }}
      >
        <DamageDuringRouteFooter
          activeStep={activeStep}
          setActiveStep={(step) => {
            if (step < activeStep) {
              handleBackStep()
            } else {
              setActiveStep(step)
            }
          }}
          isContinueEnabled={true}
          isLoading={isLoading}
          onSubmit={
            activeStep === 0
              ? handleStepZeroSubmit
              : activeStep === 1
                ? handleStepOneSubmit
                : activeStep === 2
                  ? handleFinalSubmit
                  : undefined
          }
        />
      </Box>
    </Box>
  )
}

export default DamageDuringRoute
